import type { ProductionRun } from "./models";
import { actualSegments } from "./line-timeline";

/** Operator elapsed time has no dependency on a planned start, end or shift. */
export function operatorRunTime(run: ProductionRun, now = Date.now()) {
  const start = Date.parse(run.startedAt ?? "");
  const closed = run.closedAt === null ? now : Date.parse(run.closedAt);
  if (!Number.isFinite(start) || !Number.isFinite(closed) || !Number.isFinite(now)) return null;
  const end = Math.max(start, Math.min(now, closed));
  if (run.terminalSummary) {
    const snapshot = run.terminalSummary.metrics.timing;
    const observed = Date.parse(snapshot.observedAt);
    if (!Number.isFinite(observed)) return null;
    const snapshotEnd = Math.max(start, run.closedAt === null ? observed : Math.min(observed, closed));
    const from = Math.min(end, snapshotEnd), to = Math.max(end, snapshotEnd);
    const intervals = run.downtimes.filter(stop => stop.endedAt === null)
      .map(stop => ({ start: Math.max(from, start, Date.parse(stop.startedAt)), end: to }))
      .filter(interval => Number.isFinite(interval.start) && interval.end > interval.start)
      .sort((left, right) => left.start - right.start);
    let coveredUntil = from, delta = 0;
    for (const interval of intervals) {
      delta += Math.max(0, interval.end - Math.max(coveredUntil, interval.start));
      coveredUntil = Math.max(coveredUntil, interval.end);
    }
    const totalMilliseconds = end - start;
    const downtimeMilliseconds = Math.max(0, Math.min(totalMilliseconds,
      snapshot.downtimeMilliseconds + (end >= snapshotEnd ? delta : -delta)));
    return { totalMilliseconds, downtimeMilliseconds, productionMilliseconds: Math.max(0, totalMilliseconds - downtimeMilliseconds) };
  }
  const segments = actualSegments(run, { start, end }, end);
  const downtimeMilliseconds = segments.filter(item => item.kind === "downtime")
    .reduce((total, item) => total + item.end - item.start, 0);
  const totalMilliseconds = end - start;
  return { totalMilliseconds, downtimeMilliseconds, productionMilliseconds: Math.max(0, totalMilliseconds - downtimeMilliseconds) };
}
